import { useState, type FormEvent } from 'react'
import { useDisableHotel, useHotelConfiguration } from '@/api/queries'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ErrorState } from '@/components/ui/EmptyState'
import { Field, KeyValueList } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'

/** Looks a hotel up in the configuration API, then disables its RateScreener subscription. */
export function DisableHotelDialog({ onClose }: { onClose: () => void }) {
  const [hotelId, setHotelId] = useState('')
  const [confirm, setConfirm] = useState(false)
  const lookup = useHotelConfiguration()
  const disable = useDisableHotel()
  const toast = useToast()

  const id = Number(hotelId)
  const valid = Number.isInteger(id) && id > 0
  const summary = disable.data ?? lookup.data

  const check = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    disable.reset()
    lookup.mutate(id)
  }

  const run = async () => {
    setConfirm(false)
    try {
      const result = await disable.mutateAsync(id)
      toast.notify(result.enabled ? `Hotel ${id} is still enabled` : `Hotel ${id} disabled`, result.enabled ? 'error' : 'success')
    } catch (err) {
      toast.error(err, 'Could not disable the hotel')
    }
  }

  return (
    <Modal
      title="Disable a hotel"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="danger" disabled={!summary?.enabled || summary.hotelId !== id} loading={disable.isPending} onClick={() => setConfirm(true)}>
            Disable hotel
          </Button>
        </>
      }
    >
      <form onSubmit={check} className="u-row" style={{ alignItems: 'flex-end' }}>
        <Field label="Hotel id" htmlFor="disable-hotel-id">
          <input
            id="disable-hotel-id"
            type="number"
            min={1}
            className="htl-text-input htl-text-input--full-width"
            value={hotelId}
            autoFocus
            onChange={(e) => {
              setHotelId(e.target.value)
              lookup.reset()
              disable.reset()
            }}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!valid} loading={lookup.isPending}>
          Check
        </Button>
      </form>
      {lookup.error && <ErrorState error={lookup.error} />}
      {summary && (
        <div style={{ marginTop: 16 }}>
          <KeyValueList
            items={[
              { label: 'Hotel', value: summary.hotelId },
              { label: 'RateScreener', value: <Badge tone={summary.enabled ? 'ok' : 'neutral'}>{summary.enabled ? 'enabled' : 'disabled'}</Badge> },
              { label: 'Premium', value: summary.isPremium ? 'yes' : 'no' },
              { label: 'Notification email', value: summary.notificationEmail ?? '–' },
            ]}
          />
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={`Disable hotel ${id}`}
          message="The hotel stops being screened and its notifications stop. Re-enabling must be done from the configuration API."
          confirmLabel="Disable"
          danger
          onCancel={() => setConfirm(false)}
          onConfirm={() => void run()}
        />
      )}
    </Modal>
  )
}
